import algoliasearch from 'algoliasearch';
import algoliasearchHelper from 'algoliasearch-helper';
import get from 'lodash.get';
import partition from 'lodash.partition';
import parseHash from './helpers/parse-hash';
import Renderer from './helpers/renderer';
import setUrlParams from './helpers/set-url-params';

const { console, debounce, location } = window;
let query = ''; // For storing/comparing query values

export default class Algolia {
  constructor(config) {
    this.config = config;
    this.config.HITS_PER_PAGE = 100; // Overwriting to force 100
    this.inputField = document.getElementById('q');

    this.initAlgolia();
    this.initHelper();
    this.initRenderer();
    this.initQueryObserver();
    this.initWithUrlHash();

    this.helper
      .on('change', setUrlParams)
      .on('error', console.error)
      .on('result', this.renderer.render.bind(this.renderer))
      .search();
  }

  initAlgolia() {
    this.algolia = algoliasearch(this.config.APPLICATION_ID, this.config.SEARCH_ONLY_API_KEY);
  }

  initHelper() {
    // Split up our config facets based on if they're regular or disjunctive facets
    const [facets, disjunctiveFacets] = partition(this.config.FACET_CONFIG, this.isFacet);

    const params = {
      hitsPerPage: this.config.HITS_PER_PAGE,
      maxValuesPerFacet: this.config.MAX_VALUES_PER_FACET,
      facets: facets.map(this.getFacetName),
      disjunctiveFacets: disjunctiveFacets.map(this.getFacetName)
    };

    this.helper = algoliasearchHelper(this.algolia, this.config.INDEX_NAME, params);
  }

  initRenderer() {
    const options = {
      config: this.config,
      helper: this.helper
    };

    this.renderer = new Renderer(options);
  }

  // Watch for query field changes
  initQueryObserver() {
    this.inputField.addEventListener('keyup', debounce(this.observeQueryValue.bind(this), 500));
  }

  // Set initial helper state based on url hash
  initWithUrlHash() {
    this.hash = parseHash(location.hash);

    if (Object.keys(this.hash).length === 0) return;

    const q = get(this.hash, 'q', false);
    const page = get(this.hash, 'page', 1);
    const facets = get(this.hash, 'facets', false);

    if (q) {
      this.inputField.value = q;
      this.helper.setQuery(q);
    }

    if (facets) {
      facets.forEach((facet) => {
        const { name, value } = facet;
        this.helper.toggleRefine(name, value, false);
      });
    }

    this.helper.setCurrentPage(page - 1);
  }

  getFacetName(facet) {
    return facet.name;
  }

  isFacet(facet) {
    return !facet.disjunctive;
  }

  // Start a search if the query field value has changed
  observeQueryValue() {
    if (this.inputField.value === query) return;

    query = this.inputField.value;

    this.helper
      .setQuery(query)
      .search();
  }
}
